import React from 'react';
import propTypes from 'prop-types';
import { ChevronLeft, Megaphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Button from 'tailwind-components/Button';
import Separator from 'tailwind-components/Separator/Separator';
import useBelowBreakpoint from 'hooks/useBelowBreakpoint';
import ContentWithSidebar from 'tailwind-components/ContentWithSidebar/ContentWithSidebar';
import SimpleContainer from 'tailwind-components/SimpleContainer/SimpleContainer';
import HtmlContent from 'tailwind-components/HtmlContent/HtmlContent';
import SelectedCarrierWithStates from './SelectedCarrierWithStates';
import SelectedCarrierSidebar from './SelectedCarrierSidebar';
import SelectedCarrierWithoutStates from './SelectedCarrierWithouStates';
import { workDriveEmbedSrc } from 'helpers/tableSpecialCells/embedding';
import { shouldShowOepUpdateIcon } from 'helpers/oepUpdates';

export default function SelectedCarrierDetails({
  handleResetActiveCarrier,
  activeCarrier,
  onCarrierClick
}) {
  const isBelowLg = useBelowBreakpoint('lg');
  const navigate = useNavigate();
  const user = useSelector(state => state.user.zohoData);

  const considerStates = activeCarrier.carrierConsidersStates;
  const showOepLink = shouldShowOepUpdateIcon(activeCarrier.carrierData, user);

  const mainContent = considerStates ? (
    <SelectedCarrierWithStates activeCarrier={activeCarrier} />
  ) : (
    <SelectedCarrierWithoutStates />
  );

  return (
    <div className='show-scrollbar flex flex-col gap-4 rounded-xl bg-white p-4'>
      <div className='w-full flex gap-2 justify-between'>
        <Button
          data-tour='carrier-detail-back'
          onClick={handleResetActiveCarrier}
          variant='secondary'
          size='small'
          icon={<ChevronLeft />}
          text='Back'
        />
        <div className='flex items-center gap-2'>
          {showOepLink && (
            <Button
              onClick={() =>
                navigate(
                  `/oep-updates?carrier=${encodeURIComponent(activeCarrier.name)}`
                )
              }
              variant='secondary'
              size='small'
              // a notch darker than the default secondary bg; hover is unchanged
              className='bg-primary/20'
              // amber megaphone ties it to the oep icon on the carrier cards; turns white on hover
              icon={<Megaphone className='text-amber-600' />}
              text='OEP Updates'
            />
          )}
          {isBelowLg && (
            <Button
              onClick={() => onCarrierClick(activeCarrier.name)}
              variant='secondary'
              size='small'
              text='See details'
            />
          )}
        </div>
      </div>
      <SimpleContainer>
        <h2 className='m-0 text-xl md:text-2xl font-bold text-gray-900 leading-none'>
          {activeCarrier.name}
        </h2>
        <HtmlContent html={activeCarrier.carrierData.description} />
      </SimpleContainer>
      <Separator variant='dashed' />
      <ContentWithSidebar
        layoutBreakpoint='xl'
        sidebarSide='left'
        disableScrollOverflow
        desktopSidebarGridClassName={
          'xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]'
        }
        mainContent={mainContent}
        sidebarContent={
          <SelectedCarrierSidebar carrier={activeCarrier.carrierData} />
        }
      />
      {activeCarrier.carrierData.workDriveFolderURL && (
        <>
          <Separator variant='dashed' />
          <div className='flex flex-col gap-4'>
            <h3 className='m-0 text-lg md:text-xl font-bold text-gray-900'>
              Resources
            </h3>
            <iframe
              title='Work Drive Folder'
              allowFullScreen
              className='h-[50dvh] w-full rounded-md border-none'
              src={workDriveEmbedSrc(
                activeCarrier.carrierData.workDriveFolderURL
              )}
            />
          </div>
        </>
      )}
    </div>
  );
}

SelectedCarrierDetails.propTypes = {
  handleResetActiveCarrier: propTypes.func.isRequired,
  activeCarrier: propTypes.object.isRequired,
  onCarrierClick: propTypes.func.isRequired
};
